interface ApiErrorDetail {
  field?: unknown;
  message?: unknown;
}

interface ApiErrorBody {
  error?: unknown;
  details?: unknown;
}

const asDetail = (value: unknown): ApiErrorDetail | null =>
  typeof value === 'object' && value !== null ? (value as ApiErrorDetail) : null;

/**
 * Turns one `{ field, message }` validation detail into a sentence. Backend
 * messages usually already name the field ("Password must contain: ..."); when
 * they don't ("Required"), the field name is prefixed so the user knows where
 * to look.
 */
const describeDetail = (detail: ApiErrorDetail): string | null => {
  const message = typeof detail.message === 'string' ? detail.message.trim() : '';
  if (!message) return null;

  const field = typeof detail.field === 'string' ? detail.field.trim() : '';
  if (!field || message.toLowerCase().startsWith(field.toLowerCase())) return message;

  return `${field.charAt(0).toUpperCase()}${field.slice(1)}: ${message}`;
};

/**
 * Builds a user-facing message from an API error response body.
 *
 * The backend's validateRequest middleware answers 400 with
 * `{ error: 'Validation failed', details: [{ field, message }] }`. Showing only
 * `error` left users with a bare "Validation failed" and no idea what to fix, so
 * the details are appended when present. Anything malformed falls back to
 * `error`, then to `fallback`.
 */
export function formatApiErrorMessage(body: unknown, fallback: string): string {
  const data: ApiErrorBody = typeof body === 'object' && body !== null ? body : {};
  const base = typeof data.error === 'string' && data.error.trim() ? data.error.trim() : fallback;

  if (!Array.isArray(data.details)) return base;

  const messages = data.details
    .map(asDetail)
    .flatMap(detail => (detail ? [describeDetail(detail)] : []))
    .filter((message): message is string => message !== null);

  return messages.length > 0 ? `${base}: ${messages.join('. ')}` : base;
}

/**
 * The machine-readable `code` an API failure carries, if it carries one. Read
 * structurally rather than by instance check: the error classes live in the
 * service modules, which import this one.
 */
function errorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;

  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

/**
 * Copy for the refusals a management surface can provoke — the superadmin
 * console (§4.3, §6.5–6.6) and the in-game member list alike, since both drive
 * the same island endpoints. Each one is a rule the server enforces; saying
 * which rule was hit is the difference between a usable screen and a red box
 * reading "Conflict".
 */
const ADMIN_MESSAGES: Record<string, string> = {
  last_superadmin: 'The platform must keep at least one active superadmin.',
  self_action: 'You cannot do that to your own account.',
  user_active: 'Only a deactivated account can have its email changed.',
  user_inactive: 'That account is deactivated. Reactivate it first.',
  email_taken: 'Another account already uses that email address.',
  account_exists: 'An account already exists for that email address.',
  rate_limited: 'A lot of invitations have gone out in the last hour. Try again later.',
  // The invitation row exists; only delivery failed, so Resend is the fix.
  email_failed: 'The invitation was created but the email could not be sent. Try Resend.',
  superadmin_self_access: 'Superadmins cannot grant themselves or other superadmins island access.',
  organization_has_islands: 'Move or delete this organization\u2019s islands before deleting it.',
  organization_name_taken: 'Another organization already uses that name.',
  organization_not_found: 'That organization no longer exists.',
  room_not_found: 'That island no longer exists.',
  user_not_found: 'That account no longer exists.',
  last_admin: 'An island must keep at least one active admin.',
  member_not_found: 'That person is no longer a member of this island.',
  // Thrown only for the actor's own lapsed access, so it is addressed to them —
  // word for word what `useInvitations` says for the same code.
  guest_expired: 'Your guest access to this island has expired.',
  forbidden: 'You do not have permission to do that.'
};

/**
 * Turn a management failure into a sentence, falling back to what was thrown.
 * A terse body — `{ "code": "last_admin", "error": "Conflict" }` — must not
 * reach the user as "Conflict".
 */
export function describeAdminError(error: unknown, fallback: string): string {
  const code = errorCode(error);
  if (code && ADMIN_MESSAGES[code]) {
    return ADMIN_MESSAGES[code];
  }

  return error instanceof Error ? error.message : fallback;
}
